import { useState, useEffect, useRef } from "react";
import { Menu } from "lucide-react";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import WfSetupGuard from "../WfSetupGuard";
import GlobalSetupNotification from "../common/GlobalSetupNotification";
import SubscriptionBanner from "../../Moduels/Subscription/SubscriptionBanner";
import BrixigoPageLoader from "../common/BrixigoPageLoader";
import BackButton from "../common/BackButton";
import OrgProfileGate from "../setup/OrgProfileGate";
import { setupAutoScrollAlerts } from "../../hooks/setupAutoScrollAlerts";
import "../../styles/Layout.css";
import "../../styles/orgProfileWizard.css";
import {
  isSalaryTemplateHandoffPath,
  isSettingsPath,
  rememberSettingsReturnPath,
} from "../settings/settingsExit";
import {
  clearPipelineState,
  isPipelineActive,
  isPipelineLocation,
} from "../../Moduels/Salary/v2/enterprise/components/salaryTemplatePipeline";

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [navLoading, setNavLoading] = useState(false);
  const [viewport, setViewport] = useState({ isMobile: false, isTablet: false, isDesktop: true });
  const location = useLocation();
  const navigate = useNavigate();

  // Navbar visibility: show on Dashboard and Settings sub-pages (not settings home which has its own header).
  const isSettingsRoute = isSettingsPath(location.pathname);
  const isSettingsHome = isSettingsRoute && location.pathname === "/settings" && !location.search.includes("tab=");
  const showNavbar = location.pathname === "/dashboard" || location.pathname === "/dashboard/" || (isSettingsRoute && !isSettingsHome);
  // Only /settings/* pages render inside SettingsLayout, which owns its own scroller (.sw-content).
  // Other settings-sidebar routes (/statutory/*, /salary-v2/*, /tools/*, ...) must scroll in .content-area.
  const normalizedPath = location.pathname.replace(/\/+$/, "") || "/";
  const isSettingsWorkspace = normalizedPath === "/settings" || normalizedPath.startsWith("/settings/");

  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    const prev = prevPathRef.current;
    const curr = location.pathname;
    if (isSettingsPath(curr) && !isSettingsPath(prev)) {
      rememberSettingsReturnPath(prev);
    }
    prevPathRef.current = curr;
  }, [location.pathname]);

  // Leaving the salary-structure wizard — including mid-edit — must stay on the
  // page the user chose. A leftover ?pipeline= URL must not reopen the editor.
  useEffect(() => {
    if (isSalaryTemplateHandoffPath(location.pathname)) return undefined;
    if (isPipelineLocation(location.pathname, location.search)) {
      if (!isPipelineActive()) {
        navigate({ pathname: location.pathname, search: "" }, { replace: true });
      }
      return undefined;
    }
    if (isPipelineActive()) clearPipelineState();
    return undefined;
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth || 0;
      const isMobile = w <= 768;
      const isTablet = w > 768 && w <= 1024;
      setViewport({ isMobile, isTablet, isDesktop: w > 1024 });
      if (!isMobile) setSidebarOpen(false);
      if (isMobile) setSidebarCollapsed(false);
    };
    compute();
    window.addEventListener("resize", compute, { passive: true });
    return () => window.removeEventListener("resize", compute);
  }, []);

  // Close mobile drawer whenever the route changes (smooth UX after nav clicks)
  useEffect(() => {
    if (viewport.isMobile) setSidebarOpen(false);
  }, [location.pathname, viewport.isMobile]);

  // Brixigo loader on every route change
  useEffect(() => {
    setNavLoading(true);
    const t = setTimeout(() => setNavLoading(false), 480);
    return () => clearTimeout(t);
  }, [location.pathname]);

  // After button clicks, scroll to newly shown alerts/errors anywhere in the app.
  useEffect(() => {
    const root = document.getElementById("main-region");
    if (!root) return undefined;
    return setupAutoScrollAlerts(root);
  }, [location.pathname]);

  const toggleSidebar = () => {
    if (viewport.isMobile) {
      setSidebarOpen((v) => !v);
      return;
    }
    setSidebarCollapsed((v) => {
      const next = !v;
      const main = document.getElementById("main-region");
      if (main) {
        next ? main.classList.add("sidebar-collapsed") : main.classList.remove("sidebar-collapsed");
      }
      return next;
    });
  };

  return (
    <div className={`layout-shell ${showNavbar ? "with-navbar" : "no-navbar"}`}>
      <div
        className={`mobile-overlay ${viewport.isMobile && sidebarOpen ? "visible" : ""}`}
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />

      <div
        className={`sidebar-region ${viewport.isMobile && sidebarOpen ? "mobile-open" : ""} ${sidebarCollapsed ? "collapsed" : ""}`}
      >
        <Sidebar
          mobileOpen={sidebarOpen}
          isMobile={viewport.isMobile}
          onRequestClose={() => setSidebarOpen(false)}
          onCollapsedChange={(c) => {
            setSidebarCollapsed(c);
            const main = document.getElementById("main-region");
            if (main) {
              c ? main.classList.add("sidebar-collapsed") : main.classList.remove("sidebar-collapsed");
            }
          }}
        />
      </div>

      <div className="main-region" id="main-region">
        {showNavbar && (
          <div className={`layout-navbar-wrap ${mounted ? "enter-navbar" : ""}`}>
            <Navbar toggle={toggleSidebar} />
          </div>
        )}

        {/* Floating menu trigger - shown on mobile when navbar is hidden so the
            user can still open the sidebar drawer. */}
        {!showNavbar && viewport.isMobile && (
          <button
            type="button"
            className={`floating-menu-btn ${sidebarOpen ? "is-open" : ""}`}
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label="Open navigation menu"
          >
            <Menu size={20} />
          </button>
        )}

        <main
          className={`content-area ${mounted ? "enter-content" : ""} ${showNavbar ? "" : "full-bleed"} ${isSettingsWorkspace ? "settings-embedded" : ""}`}
        >
          <GlobalSetupNotification />
          <SubscriptionBanner />
          {navLoading && <BrixigoPageLoader overlay message="Loading page…" />}
          <div
            className={`page-wrapper ${isSettingsWorkspace ? "page-wrapper--settings" : isSettingsRoute ? "page-wrapper--settings-flow" : ""}`}
            key={location.pathname}
          >
            {!isSettingsWorkspace && <BackButton />}
            <OrgProfileGate>
              <WfSetupGuard>
                <Outlet />
              </WfSetupGuard>
            </OrgProfileGate>
          </div>
        </main>
      </div>
    </div>
  );
}
